'use client';

import React from 'react';
import { cn } from '@/lib/cn';
import { CheckboxIcon } from './CheckboxIcon';

export interface SelectionCellProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'title'> {
  /** Main label (Body 2 / 14px). */
  title: React.ReactNode;
  /** Optional subtitle (Caption / 12px). */
  subtitle?: React.ReactNode;
  /** Whether the cell is selected (controls active background + checkbox state). */
  selected?: boolean;
  /** Optional leading icon (24px). */
  leadingIcon?: React.ReactNode;
  /** When true, increases left padding (used for nested/offset rows). */
  offset?: boolean;
  /** Click handler (typically toggles selected state). */
  onToggle?: () => void;
  /** When true, disables interaction with the cell. */
  disabled?: boolean;
}

/**
 * SelectionCell — pixel-aligned to the design system “Selection cell” pattern.
 */
export function SelectionCell({
  title,
  subtitle,
  selected = false,
  leadingIcon,
  offset = false,
  onToggle,
  className,
  onClick,
  disabled = false,
  ...props
}: SelectionCellProps) {
  return (
    <div
      className={cn(
        // layout
        'flex items-center gap-4',
        // spacing
        offset ? 'pl-6 pr-2' : 'px-2',
        'py-3',
        // shape
        'rounded-xl',
        // background (matches Figma: default/hover + selected/selected-hover)
        selected
          ? 'bg-active hover:bg-active-hover active:bg-active-hover'
          : 'bg-primary hover:bg-primary-hover active:bg-primary-hover',
        // interaction
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        // sizing
        'w-full',
        className,
      )}
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={event => {
        if (disabled) return;
        onClick?.(event);
        if (event.defaultPrevented) return;
        onToggle?.();
      }}
      onKeyDown={event => {
        if (disabled) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onToggle?.();
      }}
      {...props}
    >
      {leadingIcon ? (
        <div className={cn('w-6 h-6 shrink-0 flex items-center justify-center')}>{leadingIcon}</div>
      ) : null}

      <div className={cn('flex-1 min-w-0 flex flex-col gap-1 justify-center')}>
        <span className={cn('text-body-2 text-primary truncate')}>{title}</span>
        {subtitle ? (
          <span className={cn('text-caption text-secondary truncate')}>{subtitle}</span>
        ) : null}
      </div>

      <div className={cn('shrink-0')}>
        <CheckboxIcon checked={selected} />
      </div>
    </div>
  );
}

export default SelectionCell;
